import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MyMessages,
  ConversationModal,
  MessageModal,
} from "../../interfaces/MessageObject";
import MyMessagesThunk from "../../services/thunks/MyMessagesThunk";
import { SampleMyMessages } from "../../dummyData/SampleMyMessages";
import { resetAllState } from "./resetAllState";

// Define the initial state for MyMessages
// const initialState: MyMessages = SampleMyMessages;

// Create a slice for MessagesDb
const messagesSlice = createSlice({
  name: "MessagesDb",
  initialState: {
    data: null,
    loadingMap: {} as any,
    errorMap: {} as any,
    successMap: {} as any,
    // Count of conversations awaiting my reply. Authoritative value comes from
    // the backend (fetchYetToRespondCount); increment/decrement are optimistic
    // nudges that the next authoritative fetch reconciles.
    yetToRespondCount: 0,
  },
  reducers: {
    // Action to clear archive status
    clearArchiveStatus(state) {
      state.loadingMap.makeOfferInactive = null;
      state.successMap.makeOfferInactive = null;
      state.errorMap.makeOfferInactive = null;
    },

    // Action to insert a new message into a specific conversation
    insertMessage(
      state,
      action: PayloadAction<{ conversationId: string; message: MessageModal }>
    ) {
      const { conversationId, message } = action.payload;

      // Find the conversation by ID in activeConversations
      const conversation = state.data.activeConversations.find(
        (conv) => conv.productId === conversationId
      );

      if (conversation) {
        conversation.messages.push(message);
      }
    },

    // Action to set all conversations (active and inactive)
    setConversations(state, action: PayloadAction<MyMessages>) {
      state.data = action.payload;
    },

    // Appends a message that arrived live via the Firestore inbox listener to
    // the matching conversation (keyed by offerId, across active + archived).
    // Deduped by messageId so a re-emitted snapshot never doubles a message.
    appendLiveMessage(
      state,
      action: PayloadAction<{ offerId: string; message: MessageModal }>
    ) {
      const { offerId, message } = action.payload;
      const data: any = state.data;
      if (!data) return;

      const lists = [data.activeConversations, data.inActiveConversations];
      for (const list of lists) {
        const conversation = list?.find(
          (c: ConversationModal) => c.offerId === offerId
        );
        if (conversation) {
          const exists = conversation.messages.some(
            (m: MessageModal) => m.messageId === message.messageId
          );
          if (!exists) conversation.messages.push(message);
          return;
        }
      }
      // Unknown offerId (a brand-new conversation): left to the next REST load
      // to surface. Intentionally a no-op here to avoid refetch loops.
    },

    // Optimistic +1 when an incoming message newly flips a conversation to
    // "my turn". The authoritative fetch reconciles the exact value.
    incrementYetToRespond(state) {
      state.yetToRespondCount += 1;
    },

    // Optimistic -1 (clamped at 0) when I respond, before the authoritative
    // fetch confirms.
    decrementYetToRespond(state) {
      state.yetToRespondCount = Math.max(0, state.yetToRespondCount - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle pending state for fetching messages
      .addCase(MyMessagesThunk.fetchMyMessages.pending, (state) => {
        state.loadingMap.fetchMyMessages = "Loading messages...";
        state.errorMap.fetchMyMessages = null;
        state.successMap.fetchMyMessages = null;
      })
      // Handle fulfilled state for fetching messages
      .addCase(
        MyMessagesThunk.fetchMyMessages.fulfilled,
        (state, action: any) => {
          state.loadingMap.fetchMyMessages = null;
          state.data = action.payload as MyMessages;
          // state.successMap.fetchMyMessages = "Messages loaded"; // Update the state with fetched conversations
        }
      )
      // Handle rejected state for fetching messages
      .addCase(MyMessagesThunk.fetchMyMessages.rejected, (state, action) => {
        state.loadingMap.fetchMyMessages = null;
        state.errorMap.fetchMyMessages = "Error fetching messages";
        state.successMap.fetchMyMessages = null;
      })
      // Handle pending state for fetching archived messages
      .addCase(MyMessagesThunk.fetchArchivedMessages.pending, (state) => {
        state.loadingMap.fetchArchivedMessages = "Loading archived messages...";
        state.errorMap.fetchArchivedMessages = null;
        state.successMap.fetchArchivedMessages = null;
      })
      // Handle fulfilled state for fetching archived messages
      .addCase(
        MyMessagesThunk.fetchArchivedMessages.fulfilled,
        (state, action: any) => {
          state.loadingMap.fetchArchivedMessages = null;
          if (state.data) {
            state.data.inActiveConversations = action.payload as ConversationModal[];
          }
        }
      )
      // Handle rejected state for fetching archived messages
      .addCase(MyMessagesThunk.fetchArchivedMessages.rejected, (state, action) => {
        state.loadingMap.fetchArchivedMessages = null;
        state.errorMap.fetchArchivedMessages = "Error fetching archived messages";
        state.successMap.fetchArchivedMessages = null;
      })
      // Handle pending state for sending a message
      .addCase(MyMessagesThunk.sendMessage.pending, (state) => {
        state.loadingMap.sendMessage = "Sending message...";
        state.errorMap.sendMessage = null;
      })
      // Handle fulfilled state for sending a message
      .addCase(MyMessagesThunk.sendMessage.fulfilled, (state, action: any) => {
        state.loadingMap.sendMessage = null;
      })
      // Handle rejected state for sending a message
      .addCase(MyMessagesThunk.sendMessage.rejected, (state, action: any) => {
        state.loadingMap.sendMessage = null;
        state.errorMap.sendMessage = "Error sending the message"; // Store the error message
      })
      // Handle pending state for making an offer
      .addCase(MyMessagesThunk.makeOffer.pending, (state) => {
        state.loadingMap.makeOffer = "Making an offer";
        state.errorMap.makeOffer = null;
      })
      // Handle fulfilled state for making an offer
      .addCase(MyMessagesThunk.makeOffer.fulfilled, (state, action: any) => {
        state.loadingMap.makeOffer = null;
        // The fetchMyMessages call will update the state with new conversation
      })
      // Handle rejected state for making an offer
      .addCase(MyMessagesThunk.makeOffer.rejected, (state, action: any) => {
        state.loadingMap.makeOffer = false;
        state.errorMap = "Error making an offer"; // Store the error message
      })
      // Handle pending state for making offer inactive
      .addCase(MyMessagesThunk.makeOfferInactive.pending, (state) => {
        state.loadingMap.makeOfferInactive = "Archiving conversation...";
        state.errorMap.makeOfferInactive = null;
        state.successMap.makeOfferInactive = null;
      })
      // Handle fulfilled state for making offer inactive
      .addCase(MyMessagesThunk.makeOfferInactive.fulfilled, (state, action: any) => {
        state.loadingMap.makeOfferInactive = null;
        state.successMap.makeOfferInactive = "Conversation archived successfully";
        // The fetchMyMessages call in the thunk will update the state
      })
      // Handle rejected state for making offer inactive
      .addCase(MyMessagesThunk.makeOfferInactive.rejected, (state, action: any) => {
        state.loadingMap.makeOfferInactive = null;
        state.errorMap.makeOfferInactive = "Error archiving conversation";
        state.successMap.makeOfferInactive = null;
      })
      // Authoritative "yet to respond" count — the source of truth the
      // optimistic increment/decrement reconcile to.
      .addCase(
        MyMessagesThunk.fetchYetToRespondCount.fulfilled,
        (state, action: any) => {
          state.yetToRespondCount = action.payload ?? 0;
        }
      )
      .addCase(resetAllState, () => ({
        data: null,
        loadingMap: {},
        errorMap: {},
        successMap: {},
        yetToRespondCount: 0,
      }));
  },
});

// Export actions
export const {
  insertMessage,
  setConversations,
  clearArchiveStatus,
  appendLiveMessage,
  incrementYetToRespond,
  decrementYetToRespond,
} = messagesSlice.actions;
export default messagesSlice.reducer;
